




























































































































import { Component, Ref, Vue, Watch } from 'vue-property-decorator'
import { getModule } from 'vuex-module-decorators'
import tournamentStore from '@/modules/tournament/tournament.store'
import userStore from '@/modules/auth/auth.store'
import AppButton from '@/modules/shared/components/AppButton'
import { now } from '@/utils/time'
import { TournamentAPI, TournamentState } from '@/api/tournament'
import ServerError from '@/utils/server_error'
import { HubConnection } from '@microsoft/signalr'
import { CancellationToken } from 'builder-util-runtime'
import { getRealtimeConnection } from '@/utils/realtime'
import { CancelError } from '@/utils/cancel'
import lottie from 'lottie-web'
import { TournamentTeamRole, TournamentTeamState } from '@/api/tournamentTeam'

enum TournamentCheckInState {
  NOT_VISIBLE = 'NotVisible',
  PENDING = 'Pending',
  CHECKED_IN = 'CheckedIn',
  ALL_CHECKED_IN = 'AllCheckedIn'
}

@Component({
  components: {
    AppButton
  }
})
export default class TournamentCheckInOverlay extends Vue {
  tournamentModule = getModule(tournamentStore, this.$store)
  userModule = getModule(userStore, this.$store)

  // Realtime connections
  realtimeConnection!: HubConnection
  realtimeConnectionCancellationToken = new CancellationToken()

  state: TournamentCheckInState = TournamentCheckInState.NOT_VISIBLE

  // Dialog visibility
  isTeamListDialogVisible: boolean = false

  loading: boolean = false
  countdown: string = ''
  updateCountdownTimer: NodeJS.Timer | undefined = undefined

  // check in animation
  @Ref('checkLottieAnimation')
  checkLottieAnimation!: HTMLDivElement

  readyForMatchAnimation: boolean = true
  checkInAnimationVisible: boolean = false

  created() {
    this.subscribeRealtimeEvents().then()

    // timer
    this.updateCountdownTimer = setInterval(this.updateCountdown, 500)
    this.updateCountdown()
  }

  mounted() {
    this.updateState()
    this.onVisibilityChanged()
  }

  beforeDestroy() {
    if (this.updateCountdownTimer) {
      clearInterval(this.updateCountdownTimer)
      this.updateCountdownTimer = undefined
    }
    this.unsubscribeRealtimeEvents()

    this.tournamentModule.setTournamentCheckInOverlayVisible(false)
  }

  /* Watchers */
  @Watch('tournament')
  onTournamentChanged() {
    this.updateState()
  }

  @Watch('tournament.state')
  async onTournamentStateChanged(
    newVal: TournamentState,
    oldVal: TournamentState
  ) {
    await this.tournamentModule.updateJoinedTournaments()
    this.updateState()
  }

  @Watch('tournamentTeam')
  onTournamentTeamChanged() {
    this.updateState()
  }

  @Watch('state')
  onStateChanged(
    state: TournamentCheckInState,
    oldState: TournamentCheckInState
  ) {
    if (
      state === TournamentCheckInState.CHECKED_IN &&
      this.checkInAnimationVisible
    ) {
      this.readyForMatchAnimation = false
      setTimeout(() => {
        const anim = lottie.loadAnimation({
          container: this.checkLottieAnimation,
          renderer: 'svg',
          loop: false,
          autoplay: true,
          animationData: require('@/assets/lottie/5785-checkmark.json')
        })
        anim.setSpeed(0.5)
        anim.addEventListener('complete', () => {
          this.readyForMatchAnimation = true
          setTimeout(() => {
            anim.destroy()
            this.checkInAnimationVisible = false
          }, 800)
        })
      })
    }

    this.setMusicPlay(this.state === TournamentCheckInState.PENDING)
  }

  @Watch('isVisible')
  onVisibilityChanged() {
    this.tournamentModule.setTournamentCheckInOverlayVisible(this.isVisible)
  }

  @Watch('hasLoggedIn')
  onLoginStateChanged() {
    this.updateState()
  }

  @Ref('audioElement')
  audioElement!: HTMLAudioElement

  /* Getters */
  get isVisible() {
    return this.state !== TournamentCheckInState.NOT_VISIBLE
  }

  /* Methods */
  async subscribeRealtimeEvents() {
    try {
      this.realtimeConnection = await getRealtimeConnection(
        this.realtimeConnectionCancellationToken
      )
    } catch (err) {
      if (err instanceof CancelError) {
        return
      }

      this.$toast.error(this.$t('Landing.FailConnectServerMessage'))
      console.error(err)
    }

    this.realtimeConnection.on(
      'tournamentCheckedIn',
      this.onTournamentCheckedIn
    )
    this.realtimeConnection.on(
      'tournamentCheckedInAll',
      this.onTournamentCheckedInAll
    )
  }

  unsubscribeRealtimeEvents() {
    this.realtimeConnectionCancellationToken.cancel()
    if (this.realtimeConnection) {
      this.realtimeConnection.off(
        'tournamentCheckedIn',
        this.onTournamentCheckedIn
      )
      this.realtimeConnection.off(
        'tournamentCheckedInAll',
        this.onTournamentCheckedInAll
      )
    }
  }

  close() {
    this.state = TournamentCheckInState.NOT_VISIBLE
  }

  async onTournamentCheckInClick() {
    this.loading = true
    this.checkInAnimationVisible = true
    this.state = TournamentCheckInState.CHECKED_IN

    try {
      await TournamentAPI.tournamentCheckIn(
        this.tournamentTeam!.tournamentTeamId
      )
    } catch (e) {
      if (e instanceof ServerError) {
        if (e.getMessage() !== 'AlreadyCheckedIn') {
          this.$toast.error(e.getMessage())
        } else return
      } else {
        this.$toast.error('System error')
      }
      this.state = TournamentCheckInState.PENDING
      console.error(e)
    } finally {
      this.loading = false
    }
  }

  onCheckBracketClick() {
    this.tournamentModule.setAutomatedTournamentBracketDialogVisible(true)
  }

  onCheckPlayerListClick() {
    this.tournamentModule.setAutomatedTournamentTeamListDialogVisible(true)
  }

  onCloseClick() {
    this.close()
  }

  onTournamentCheckedIn(isAllCheckedIn: boolean) {
    if (
      !this.tournamentTeam ||
      this.tournamentTeam.teamState !== TournamentTeamState.Registered
    )
      return

    if (isAllCheckedIn) {
      setTimeout(() => {
        this.state = TournamentCheckInState.ALL_CHECKED_IN
      }, 2500) // animation delay total time
    } else {
      this.checkInAnimationVisible = true
      this.state = TournamentCheckInState.CHECKED_IN
    }
  }

  onTournamentCheckedInAll() {
    if (
      !this.tournament ||
      !this.tournamentTeam ||
      this.tournamentTeam.teamState !== TournamentTeamState.Registered
    ) {
      return
    }

    setTimeout(() => {
      this.state = TournamentCheckInState.ALL_CHECKED_IN
    }, 2500) // animation delay total time
  }

  updateState() {
    if (!this.hasLoggedIn) {
      this.state = TournamentCheckInState.NOT_VISIBLE
      return
    }

    // All checked in means the check in process is over. Should not do any further update
    if (this.state === TournamentCheckInState.ALL_CHECKED_IN) {
      return
    }

    if (
      !this.tournament ||
      !this.tournamentTeam ||
      this.tournamentTeam.role !== TournamentTeamRole.Captain ||
      this.tournament.state !== TournamentState.WaitingForCheckIn ||
      this.tournamentTeam.teamState !== TournamentTeamState.Registered
    ) {
      // do not hide overlay if check in animation is playing
      if (this.checkInAnimationVisible) {
        return
      }
      this.state = TournamentCheckInState.NOT_VISIBLE
      return
    }

    if (this.tournamentTeam.isTournamentCheckedIn) {
      this.state = TournamentCheckInState.CHECKED_IN
    } else {
      this.state = TournamentCheckInState.PENDING
    }
  }

  /* Computed Properties */
  get tournament() {
    return this.tournamentModule.selectedTournament
  }

  get tournamentCheckInDuration() {
    if (!this.tournament) return 10
    return this.tournament.tournamentCheckInDuration
  }

  get tournamentTeam() {
    if (!this.tournament) {
      return null
    }

    return this.tournamentModule.joinedTournaments.find(
      tournament => tournament.tournamentId === this.tournament!.tournamentId
    )
  }

  get TournamentCheckInState() {
    return TournamentCheckInState
  }

  get TournamentState() {
    return TournamentState
  }

  get hasLoggedIn() {
    return this.userModule.hasLoggedIn
  }

  updateCountdown() {
    if (!this.tournament || !this.tournament.tournamentCheckInEndTime) {
      this.countdown = '--'
      return
    }

    const timeleft = Math.max(
      0,
      this.tournament.tournamentCheckInEndTime - now().getTime() / 1000
    )

    const hours = Math.floor(timeleft / 60 / 60)
    const minutes = Math.floor(timeleft / 60)
    const seconds = Math.floor(timeleft % 60)

    this.countdown = `${hours
      .toString()
      .padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  setMusicPlay(state: boolean) {
    if (!this.audioElement) {
      return
    }

    if (state) {
      this.audioElement.play()
    } else {
      this.audioElement.pause()
      this.audioElement.currentTime = 0
    }
  }
}
